import { useState, useEffect, useRef } from 'react';

// Message sets for different time ranges
const NORMAL_MESSAGES = [
  'Analyzing your request...',
  'Generating code...',
  'Building your game...',
  'Processing...',
  'Creating components...',
  'Working on it...',
  'Almost there...',
  'Thinking it through...',
  'Putting it together...',
  'Getting it done...'
];

const GOOFY_MESSAGES = [
  'Looking for reading glasses...',
  'Getting to work... eventually...',
  'Dusting off the keyboard...',
  'Warming up the typing fingers...',
  'Consulting the magic 8-ball...',
  'Brewing some code...',
  'Summoning the code spirits...',
  'Reading between the lines...',
  'Asking a friend for help...',
  'Googling "how to code"...',
  'Sipping on a cup of coffee...',
  'Checking the weather...',
  'Trying to focus...'
];

const ABSURD_MESSAGES = [
  'Teaching a hamster to code...',
  'Negotiating with the AI overlords...',
  'Sacrificing a keyboard to the gods...',
  'Channeling ancient coding spirits...',
  'Waiting for inspiration to strike...',
  'Consulting the prophecy...',
  'Aligning the cosmic variables...',
  'Translating from alien code...',
  'Waking up the sleepy algorithms...',
  'Bribing the compiler with cookies...',
  'Asking SKYNET for help...',
  'Beating HAL 9000 into submission...',
  'Casually looking around your OS...',
  'Eating your browser cookies...'
];

/**
 * Hook to manage processing state with rotating humorous messages
 * Displays different message sets based on elapsed time
 */
export function useProcessingMessages(isProcessing: boolean) {
  const [processingTime, setProcessingTime] = useState(0);
  const [currentProcessingMessage, setCurrentProcessingMessage] = useState(NORMAL_MESSAGES[0]);
  const processingStartTime = useRef<number>(0);
  const processingMessageIndex = useRef<number>(0);

  useEffect(() => {
    if (!isProcessing) return;

    // Start timer
    processingStartTime.current = Date.now();
    setProcessingTime(0);

    // Set initial message
    const initialMessages = NORMAL_MESSAGES;
    const initialIndex = Math.floor(Math.random() * initialMessages.length);
    processingMessageIndex.current = initialIndex;
    setCurrentProcessingMessage(initialMessages[initialIndex]);

    // Update timer every 100ms
    const timerInterval = setInterval(() => {
      setProcessingTime((Date.now() - processingStartTime.current) / 1000);
    }, 100);

    // Rotate through messages every 6 seconds
    const messageInterval = setInterval(() => {
      const currentTime = (Date.now() - processingStartTime.current) / 1000;
      const messages = 
        currentTime < 15 ? NORMAL_MESSAGES : 
        currentTime < 40 ? GOOFY_MESSAGES : 
        ABSURD_MESSAGES;

      let newIndex = Math.floor(Math.random() * messages.length);
      let attempts = 0;
      // Try to avoid repeating the same message
      while (messages[newIndex] === currentProcessingMessage && messages.length > 1 && attempts < 10) {
        newIndex = Math.floor(Math.random() * messages.length);
        attempts++;
      }

      processingMessageIndex.current = newIndex;
      setCurrentProcessingMessage(messages[newIndex]);
    }, 6000);

    return () => {
      clearInterval(timerInterval);
      clearInterval(messageInterval);
    };
  }, [isProcessing, currentProcessingMessage]);

  return { 
    processingTime, 
    currentProcessingMessage, 
    processingMessageIndex 
  };
}

